/**
 * 사용자 관련 Write 작업 목차
 * 
 * - getUserInfo() - 사용자 정보 조회
 * - joinUser() - 회원가입 (password 유무로 이메일/소셜 자동 분기)
 * - updateUserInfo() - 사용자 정보 업데이트
 * - setNickname() - 닉네임 설정
 * - updateUserFields() - 사용자 필드 업데이트 (범용)
 * - confirmTermsNotice() - 이용약관 개정 안내 확인 저장
 * - updateLastLogin() - 마지막 로그인 시간 업데이트
 * - handleLogin() - 로그인 처리 (통합)
 * - toggleLiveLike() - 라이브 좋아요 토글
 * - toggleArtistLike() - 아티스트 좋아요 토글
 * - toggleCommissionLike() - 커미션 좋아요 토글
 * - toggleVideoLike() - 비디오 좋아요 토글
 * - setProfileImage() - 프로필 이미지 설정
 * - removeProfileImage() - 프로필 이미지 삭제
 * - deleteProfileImageField() - profileImage 필드 삭제 (레거시 정리용)
 * - markNotificationAsRead() - 알림 읽음 처리
 * - addLevelPoints() - 레벨 포인트 추가
 * - addActivityPoints() - 활동별 자동 점수 추가
 * - trackActivityLog() - 활동 로그 기록 (API 통합)
 * - recordTrafficSource() - 유입 경로 기록 (API 통합)
 */

import apiClient, { setTokens } from "@/utils/api";
import { useUserStore } from "@/store/user";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

// ============================================
// 상수 정의
// ============================================

/**
 * 활동별 점수 정의
 */
export const ACTIVITY_POINTS = {
  POST_WRITE: 10,
  COMMENT_WRITE: 5,
  RECOMMENT_WRITE: 3,
  POST_LIKE: 1,
  COMMENT_LIKE: 1,
  POST_LIKE_CANCEL: -1,
  COMMENT_LIKE_CANCEL: -1,
};

/**
 * 사용자 정보 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<Object|null>} 사용자 정보 (존재하지 않으면 null)
 */
export async function getUserInfo(userEmail) {
  try {
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}`);
    if (response.data.success) {
      return response.data.data;
    } else {
      return response.data;
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // 사용자가 존재하지 않으면 null 반환
      return null;
    }
    console.error("사용자 정보 조회 오류:", error);
    throw error;
  }
}

/**
 * 회원가입 (password 유무로 이메일/소셜 자동 분기)
 * - password 있음: 이메일 회원가입 (Firebase Auth + Firestore)
 * - password 없음: 소셜 회원가입 (Firestore만)
 * @param {string} userEmail - 사용자 이메일
 * @param {string} nickname - 닉네임
 * @param {Object} options - 옵션 { password?, ...userData }
 * @returns {Promise<Object|void>} 이메일 회원가입 시 { email, uid, nickname, customToken, accessToken, refreshToken } 반환
 */
export async function joinUser(userEmail, nickname, options = {}) {
  try {
    const response = await apiClient.post("/api/user/join", {
      email: userEmail,
      nickname: nickname,
      ...options,
    });
    
    const data = response.data.data;
    
    // AccessToken과 RefreshToken 저장
    if (data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
    }
    
    return data;
  } catch (error) {
    console.error("회원가입 오류:", error);
    throw error;
  }
}

/**
 * 사용자 정보 업데이트
 * @param {string} userEmail - 사용자 이메일
 * @param {Object} userData - 사용자 데이터
 * @returns {Promise<void>}
 */
export async function updateUserInfo(userEmail, userData) {
  try {
    await apiClient.post("/api/user", {
      email: userEmail,
      ...userData,
    });
  } catch (error) {
    console.error("사용자 정보 업데이트 오류:", error);
    throw error;
  }
}

/**
 * 닉네임 설정
 * @param {string} userEmail - 사용자 이메일
 * @param {string} nickname - 닉네임
 * @returns {Promise<void>}
 */
export async function setNickname(userEmail, nickname) {
  try {
    await apiClient.patch(`/api/user/${encodeURIComponent(userEmail)}/nickname`, {
      nickname,
    });

    // 닉네임 수정 활동 기록
    trackActivity(ACTIVITY_TYPES.NICKNAME_UPDATE, {
      metadata: { nickname },
    });
  } catch (error) {
    console.error("닉네임 설정 오류:", error);
    throw error;
  }
}

/**
 * 사용자 필드 업데이트 (범용)
 * @param {string} userEmail - 사용자 이메일
 * @param {Object} fields - 업데이트할 필드들
 * @returns {Promise<void>}
 */
export async function updateUserFields(userEmail, fields) {
  try {
    await apiClient.patch(`/api/user/${encodeURIComponent(userEmail)}`, fields);
  } catch (error) {
    console.error("사용자 필드 업데이트 오류:", error);
    throw error;
  }
}

/**
 * 이용약관 개정 안내 확인 저장
 * @param {string} userEmail - 사용자 이메일
 * @param {string} version - 확인한 약관 버전
 * @param {string} confirmedAt - 확인 시각 ISO 문자열
 * @returns {Promise<void>}
 */
export async function confirmTermsNotice(
  userEmail,
  version,
  confirmedAt = new Date().toISOString()
) {
  if (!userEmail || !version) {
    throw new Error("userEmail과 version이 필요합니다.");
  }

  try {
    await apiClient.patch(`/api/user/${encodeURIComponent(userEmail)}`, {
      termsNoticeConfirmedVersion: version,
      termsNoticeConfirmedAt: confirmedAt,
    });
  } catch (error) {
    console.error("이용약관 개정 안내 확인 저장 오류:", error);
    throw error;
  }
}

/**
 * 마지막 로그인 시간 업데이트
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<void>}
 */
export async function updateLastLogin(userEmail) {
  try {
    await apiClient.patch(`/api/user/${encodeURIComponent(userEmail)}`, {
      lastLogin: new Date().toISOString(),
    });
  } catch (error) {
    console.error("마지막 로그인 시간 업데이트 오류:", error);
    throw error;
  }
}

/**
 * 로그인 처리 (통합)
 * - 사용자 정보 조회
 * - 마지막 로그인 시간 업데이트
 * - 로그인 히스토리 기록
 * - Custom Token 발급
 * - AccessToken 및 RefreshToken 발급
 * @param {string} userEmail - 사용자 이메일
 * @param {string} provider - 로그인 제공자 (email, google, kakao 등)
 * @param {Object} deviceInfo - 디바이스 정보 (device, browser, os, screenSize)
 * @param {string} idToken - Firebase ID Token (email provider인 경우 필수)
 * @returns {Promise<Object>} 사용자 정보 (customToken, accessToken, refreshToken 포함)
 */
export async function handleLogin(userEmail, provider = "email", deviceInfo = {}, idToken = null) {
  try {
    const response = await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/login`, {
      provider,
      device: deviceInfo.device,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screenSize: deviceInfo.screenSize,
      ...(idToken && { idToken })
    });

    const data = response.data.data;
    
    // AccessToken과 RefreshToken 저장
    if (data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
    }
    
    return data;
  } catch (error) {
    console.error("로그인 처리 오류:", error);
    throw error;
  }
}

/**
 * 라이브 좋아요 토글
 * @param {string} liveId - 라이브 ID
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태 (optional, transaction에서 자동 확인)
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleLiveLike(liveId, userEmail, isCurrentlyLiked = null) {
  try {
    const response = await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/live/${liveId}/like`, {
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 찜하기 활동 기록
    trackActivity(newLikedState ? ACTIVITY_TYPES.LIVE_LIKE : ACTIVITY_TYPES.LIVE_UNLIKE, {
      target: "live",
      targetId: liveId,
    });

    return newLikedState;
  } catch (error) {
    console.error("라이브 좋아요 오류:", error);
    throw error;
  }
}

/**
 * 아티스트 좋아요 토글
 * @param {string} artistName - 아티스트 이름
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleArtistLike(artistName, userEmail, isCurrentlyLiked) {
  try {
    const response = await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/artist/${encodeURIComponent(artistName)}/like`, {
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 찜하기 활동 기록
    trackActivity(newLikedState ? ACTIVITY_TYPES.ARTIST_LIKE : ACTIVITY_TYPES.ARTIST_UNLIKE, {
      target: "artist",
      targetId: artistName,
    });

    return newLikedState;
  } catch (error) {
    console.error("아티스트 좋아요 오류:", error);
    throw error;
  }
}

/**
 * 커미션 좋아요 토글
 * @param {string} commissionId - 커미션 ID
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleCommissionLike(commissionId, userEmail, isCurrentlyLiked) {
  try {
    const response = await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/commission/${commissionId}/like`, {
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 찜하기 활동 기록
    trackActivity(newLikedState ? ACTIVITY_TYPES.COMMISSION_LIKE : ACTIVITY_TYPES.COMMISSION_UNLIKE, {
      target: "commission",
      targetId: commissionId,
    });

    return newLikedState;
  } catch (error) {
    console.error("커미션 좋아요 오류:", error);
    throw error;
  }
}

/**
 * 비디오 좋아요 토글
 * @param {string} videoId - 비디오 ID
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleVideoLike(videoId, userEmail) {
  if (!videoId || !userEmail) {
    throw new Error("videoId와 userEmail이 필요합니다.");
  }
  let response;
  try {
    response = await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/video/${videoId}/like`);
  } catch (error) {
    console.error("비디오 좋아요 API 오류:", error);
    throw error;
  }
  // HTTP 성공 이후에는 throw 없이 결과만 반환 (활동 로깅 등 부가 로직 실패가 찜 실패로 보이지 않도록)
  try {
    const data = response?.data?.data ?? response?.data;
    const newLikedState = data?.isLiked ?? data?.liked ?? false;
    trackActivity(newLikedState ? ACTIVITY_TYPES.VIDEO_LIKE : ACTIVITY_TYPES.VIDEO_UNLIKE, {
      target: "video",
      targetId: videoId,
    }).catch(() => {});
    return Boolean(newLikedState);
  } catch (e) {
    console.warn("비디오 좋아요 응답 처리 중 오류 (찜은 성공):", e);
    return true;
  }
}

/**
 * 프로필 이미지 설정
 * @param {string} userEmail - 사용자 이메일
 * @param {File} imageFile - 이미지 파일
 * @returns {Promise<void>}
 */
export async function setProfileImage(userEmail, imageFile) {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/profile-image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (error) {
    console.error("프로필 이미지 설정 오류:", error);
    throw error;
  }
}

/**
 * 프로필 이미지 삭제
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<void>}
 */
export async function removeProfileImage(userEmail) {
  try {
    await apiClient.delete(`/api/user/${encodeURIComponent(userEmail)}/profile-image`);
  } catch (error) {
    console.error("프로필 이미지 삭제 오류:", error);
    throw error;
  }
}

/**
 * profileImage 필드 삭제 (레거시 필드 정리용)
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<void>}
 */
export async function deleteProfileImageField(userEmail) {
  try {
    await apiClient.delete(`/api/user/${encodeURIComponent(userEmail)}/field/profileImage`);
  } catch (error) {
    console.error("profileImage 필드 삭제 오류:", error);
    throw error;
  }
}

/**
 * 알림 읽음 처리
 * @param {string} notificationId - 알림 ID
 * @returns {Promise<void>}
 */
export async function markNotificationAsRead(notificationId) {
  try {
    await apiClient.patch(`/api/alarm/${notificationId}/read`);
  } catch (error) {
    console.error("알림 읽음 처리 오류:", error);
    throw error;
  }
}

/**
 * 사용자 레벨 포인트 추가/차감
 * @param {string} userEmail - 사용자 이메일
 * @param {number} points - 추가할 점수 (음수 가능)
 * @param {string} activity - 활동 유형 (로그용)
 * @returns {Promise<void>}
 */
export async function addLevelPoints(userEmail, points, activity = "") {
  if (!userEmail || points === 0) return;

  try {
    await apiClient.post(`/api/user/${encodeURIComponent(userEmail)}/points`, {
      points,
      activity,
    });

    // 현재 로그인한 사용자의 스토어 업데이트
    const userStore = useUserStore();
    if (userStore.email === userEmail) {
      userStore.addLevelPoint(points);
    }

    if (activity) {
      // const sign = points > 0 ? "+" : "";
      // console.log(`레벨 포인트 변경: ${userEmail} ${sign}${points}점 (${activity})`);
    }
  } catch (error) {
    console.error("레벨 포인트 변경 실패:", error);
    throw error;
  }
}

/**
 * 활동 유형에 따라 자동으로 점수 추가
 * @param {string} userEmail - 사용자 이메일
 * @param {string} activityType - ACTIVITY_POINTS의 키
 * @returns {Promise<void>}
 */
export async function addActivityPoints(userEmail, activityType) {
  const points = ACTIVITY_POINTS[activityType];
  if (!points) {
    console.warn(`알 수 없는 활동 유형: ${activityType}`);
    return;
  }

  await addLevelPoints(userEmail, points, activityType);
}

/**
 * 활동 로그 기록 (API 통합)
 * 로그인/비로그인 사용자 모두 처리 (백엔드에서 적절한 컬렉션에 저장)
 * @param {string} type - 활동 유형 (ACTIVITY_TYPES 참조)
 * @param {Object} options - 옵션
 * @param {string} options.target - 대상 페이지/기능
 * @param {string} options.targetId - 대상 ID
 * @param {number} options.duration - 체류 시간 (초)
 * @param {string} options.referrer - 이전 페이지
 * @param {Object} options.metadata - 추가 메타데이터
 * @param {string} options.sessionId - 세션 ID (선택)
 * @returns {Promise<void>}
 */
export async function trackActivityLog(type, options = {}) {
  try {
    const userStore = useUserStore();
    const isLoggedIn = !!userStore.email;
    
    // localhost에서는 추적하지 않음 (개발 환경)
    if (window.location.hostname.includes("localhost")) {
      return;
    }
    
    // 세션 ID 가져오기 (options에 없으면 생성)
    let sessionId = options.sessionId;
    if (!sessionId) {
      const { getSessionId } = await import("@/utils/activityTracker");
      sessionId = getSessionId();
    }
    
    // 백엔드 API 요청 데이터 구성
    const requestData = {
      sessionId,
      type,
      userEmail: isLoggedIn ? userStore.email : null,
    };
    
    // 선택적 필드 추가
    if (options.target) requestData.target = options.target;
    if (options.targetId) requestData.targetId = options.targetId;
    if (options.duration) requestData.duration = options.duration;
    if (options.referrer) requestData.referrer = options.referrer;
    if (options.metadata) requestData.metadata = options.metadata;
    
    // 비로그인 사용자인 경우 디바이스 정보 추가
    if (!isLoggedIn) {
      const { getDeviceInfo } = await import("@/utils/activityTracker");
      const deviceInfo = getDeviceInfo();
      requestData.device = deviceInfo.device;
      requestData.browser = deviceInfo.browser;
      requestData.os = deviceInfo.os;
    }
    
    // 백엔드 API 호출 (로그인/비로그인 모두 처리)
    await apiClient.post('/api/user/activity-logs', requestData);
    
  } catch (error) {
    console.error("활동 로그 기록 오류:", error);
    // 에러를 throw하지 않음 (활동 로그 실패가 앱 동작을 막지 않도록)
  }
}

/**
 * 유입 경로 기록 (API 통합)
 * 첫 방문 시 유입 경로 정보를 백엔드에 저장
 * @returns {Promise<void>}
 */
export async function recordTrafficSource() {
  try {
    // localhost에서는 추적하지 않음
    if (window.location.hostname.includes("localhost")) {
      return;
    }
    
    // 이미 이 세션에서 기록했는지 확인
    const sessionKey = "linkook_traffic_recorded";
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }
    
    const userStore = useUserStore();
    const { getTrafficSource, getDeviceInfo } = await import("@/utils/activityTracker");
    
    const trafficData = getTrafficSource();
    const deviceInfo = getDeviceInfo();
    
    // 백엔드 API 요청 데이터 구성
    const requestData = {
      ...trafficData,
      device: deviceInfo.device,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screenSize: deviceInfo.screenSize,
      userId: userStore.email || null,
    };
    
    // 백엔드 API 호출
    await apiClient.post('/api/user/traffic-source', requestData);
    
    // 세션에 기록 완료 표시
    sessionStorage.setItem(sessionKey, "true");
    
  } catch (error) {
    console.error("유입 경로 기록 오류:", error);
    // 에러를 throw하지 않음 (유입 경로 기록 실패가 앱 동작을 막지 않도록)
  }
}
